// @ts-strict-ignore
import React, { useCallback, useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { actionAtom } from 'atoms/atoms'
import { useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import { Box, toast } from '@node-space/ui-components'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useEditApiKeyMutation } from 'hooks/mutations'
import { editApiKeyDataAtom } from 'pages/ApiKeys/atoms/editApiKeyDataAtom'
import { ApiKeyCreateAPIRequest, ApiKeyRequestForm } from 'types/apiKeys'
import { BaseErrorResponse } from 'types/beneficiaries'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'
import ApiKeyCreateForm from '../../../Form/ApiKeyCreateForm'

export const ApiKeyManage = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { profile } = useProfileContext()
  const { mutate, isPending } = useEditApiKeyMutation()
  const [requestError, setRequestError] = useState('')
  const selectedApiKey = useAtomValue(editApiKeyDataAtom)
  const apiKeyAction = useAtomValue(actionAtom)

  useEffect(() => {
    track.Amp.track(AmplitudeEvent.API_KEY_EDIT, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.VIEW,
    })
  }, [])

  const [payload, setPayload] = useState<ApiKeyRequestForm>({
    description: selectedApiKey?.description ?? '',
    withdrawals: selectedApiKey?.canWithdraw ?? true,
    readOnly: selectedApiKey?.readOnly ?? false,
    allowedIps:
      selectedApiKey?.allowedIps
        ?.filter(value => value !== '')
        .map(ip => ({ value: ip, label: ip })) ?? [],
  })

  const closeModalCallback = useCallback(() => {
    setPayload(null)
    apiKeyAction?.onClose?.()
  }, [setPayload, apiKeyAction])

  const getRoles = (payload: ApiKeyRequestForm) => {
    if (payload.readOnly) {
      return profile.roles.filter(role => role.endsWith('_READ'))
    }

    if (payload.withdrawals) {
      return [...profile.roles, 'ROLE_WITHDRAWAL']
    }

    return profile.roles.filter(role => role !== 'ROLE_WITHDRAWAL')
  }

  const mappedReq = (payload: ApiKeyRequestForm): ApiKeyCreateAPIRequest => {
    const allowedIps = payload.allowedIps
      .filter(({ value }) => value !== '')
      .map(({ value }) => value)

    return {
      allowedIps: allowedIps.length ? allowedIps.join(',') : null,
      description: payload.description,
      roles: getRoles(payload),
    }
  }

  const updateKeyCallback = async (payload: ApiKeyRequestForm) => {
    mutate(
      { id: selectedApiKey.id, payload: mappedReq(payload) },
      {
        onSuccess: () => {
          closeModalCallback()
          toast({ header: t('manageApiKeys.apiKeyUpdated'), variant: 'success' })
          track.Amp.track(AmplitudeEvent.EOF_EDIT_API_KEY_SUCCESS, {
            category: AmplitudeEventCategory.MERCHANT_PORTAL,
            action: AmplitudeEventAction.CLICK,
          })
          queryClient.invalidateQueries({ queryKey: ['apiKeys'] })
        },
        onError: (error: BaseErrorResponse) => {
          if (error?.status === 400) {
            setRequestError(t('manageApiKeys.invalidRequest'))
          } else {
            setRequestError(t('oopsSomethingWentWrong'))
          }
          console.error('Error: update api key', error)
        },
      }
    )
  }

  return (
    <Box paddingT={24} className="max-w-[608px] w-full mx-auto">
      <ApiKeyCreateForm
        payload={payload}
        onSubmit={(formData: ApiKeyRequestForm) => updateKeyCallback(formData)}
        isLoading={isPending}
        requestError={requestError}
        isEditMode
        dismissAction={closeModalCallback}
      />
    </Box>
  )
}
